import { useMemo, useState } from "react";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import { Delete01Icon, Wallet04Icon } from "@hugeicons-pro/core-stroke-rounded";
import { toast } from "react-toastify";
import { IncomeItem, IncomeStatus, IncomeType } from "shared/types";
import { CombinedIncomeSubStep } from "../../../constants";
import { CombinedIncomeLayout } from "../components/Layout";
import { useCombinedIncome } from "../context";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";

const formatIncomeAmount = (income: IncomeItem) => {
  if (!income.amount) return "";
  const dollars = income.amount.cents / 100;
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: income.amount.currency || "USD",
    minimumFractionDigits: 2
  }).format(dollars);

  return `${formatted} / month`;
};

const getIncomeName = (income: IncomeItem) => {
  if (income.type === IncomeType.OtherIncome)
    return income.additional_data?.description || "Other income";
  return income.additional_data?.company_name || income.type.replace(/_/g, " ");
};

export const AddAnotherIncomeStep = () => {
  const {
    applicationId,
    applicantId,
    verification,
    updateStepMutation,
    refetchVerification,
    getNextStep,
    skipHousingAssistance,
    finishVerification,
    isFinishingVerification
  } = useCombinedIncome();

  const [isAddMoreLoading, setIsAddMoreLoading] = useState(false);
  const [isCompleteLoading, setIsCompleteLoading] = useState(false);

  const addedIncomes = useMemo(() => {
    return verification?.incomes?.filter(
      (income) =>
        ![IncomeStatus.Deleted, IncomeStatus.Pending].includes(income.status)
    );
  }, [verification?.incomes]);

  const deleteIncomeMutation = useMutation({
    mutationKey: ["DELETE_INCOME"],
    mutationFn: (incomeId: number) => {
      return instance.delete(
        `/portal/v1/applications/${applicationId}/verifications/${verification?.id}/income/${incomeId}?applicant_id=${applicantId}`
      );
    },
    onSuccess: () => {
      toast.success("Income deleted");
      refetchVerification();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete income");
    }
  });

  const handleAddAnother = async () => {
    setIsAddMoreLoading(true);
    try {
      await updateStepMutation.mutateAsync(
        {
          lastStep: CombinedIncomeSubStep.SelectIncomeType,
          currentIncomeType: null,
          currentIncomeId: null,
          company_name: "",
          job_title: "",
          start_date: "",
          supervisor: {
            first_name: "",
            last_name: "",
            contact_email: "",
            contact_phone: ""
          },
          description: "",
          paid_from: "",
          pay_period: "",
          amount: ""
        },
        {
          onSuccess: () => {
            refetchVerification();
          },
          onError: (error) => {
            toast.error(
              getErrorMessage(error) || "Failed to add another income"
            );
          }
        }
      );
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to add another income");
    } finally {
      setIsAddMoreLoading(false);
    }
  };

  const handleComplete = async () => {
    setIsCompleteLoading(true);
    try {
      const nextStep = getNextStep(CombinedIncomeSubStep.AddAnotherIncome, {
        skipHousingAssistance
      });
      if (!nextStep) return;

      if (nextStep === CombinedIncomeSubStep.Completed) {
        finishVerification();
        return;
      }

      await updateStepMutation.mutateAsync({ lastStep: nextStep });
    } catch (error) {
      toast.error(
        getErrorMessage(error) || "Failed to complete combined income"
      );
    } finally {
      setIsCompleteLoading(false);
    }
  };

  return (
    <CombinedIncomeLayout
      title="Would you like to add another income source?"
      footer={
        <>
          <Button
            variant={ButtonVariant.BASE}
            className="w-[132px]"
            onClick={handleAddAnother}
            loading={isAddMoreLoading}
            disabled={isCompleteLoading}
          >
            Add more
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-[132px]"
            onClick={handleComplete}
            loading={isCompleteLoading || isFinishingVerification}
            disabled={isAddMoreLoading || !addedIncomes?.length}
          >
            Continue
          </Button>
        </>
      }
    >
      <Show if={!!addedIncomes?.length}>
        <div className="space-y-3 mt-6">
          {addedIncomes?.map((income) => (
            <div
              key={income.id}
              className="flex items-center gap-3 p-4 border border-alpha-100 rounded-lg bg-background-1000 shadow-xs"
            >
              <div className="flex justify-center items-center bg-primary-50 w-10 h-10 rounded-lg">
                <HugeiconsIcon
                  icon={Wallet04Icon}
                  size={20}
                  className="text-primary-500"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-content-1000">
                  {getIncomeName(income)}
                </p>
                {income.amount && (
                  <p className="text-xs text-content-400">
                    {formatIncomeAmount(income)}
                  </p>
                )}
              </div>
              <IconButton
                HugeIcon={Delete01Icon}
                size={IconButtonSize.SM}
                onClick={() => deleteIncomeMutation.mutate(income.id)}
                loading={deleteIncomeMutation.isPending}
              />
            </div>
          ))}
        </div>
      </Show>
    </CombinedIncomeLayout>
  );
};
